import CheckIcon from '@mui/icons-material/Check'
import { LoadingButton } from '@mui/lab'
import { Box, Button, CircularProgress, Drawer, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { environment } from '../../../../enviroments/enviroment'
import { useNotification } from '../../../../store/redux/hooks'
import { getCurrentUser } from '../../../../store/redux/reducers/currentUserSlice'
import {
  pushNotificationSlice,
  useGetPushNotificationsQuery,
  useMarkAllPushNotificationsReadMutation,
  useMarkPushNotificationReadMutation,
} from '../../../../store/redux/reducers/pushNotificationSlice'
import { useAcknowledgeVacationMutation } from '../../../../store/redux/reducers/vacationAcknowledgementApiSlice'
import { useLazyCheckVacationExistsQuery } from '../../../../store/redux/reducers/vacationSlice'
import { createWsClient } from '../../../../store/redux/ws/wsClient'
import UserService from '../../../../store/services/UserService'
import NotificationDispatchStatus from '../../../../store/types/enums/NotificationDispatchStatus'
import { NotificationReason } from '../../../../store/types/enums/NotificationReason'
import NotificationType from '../../../../store/types/enums/NotificationType'
import PushNotification from '../../../../store/types/models/PushNotification'
import NotificationProps from '../../../../store/types/props/NotificationProps'
import { Vacation } from '../../../../store/types/utils/VacationTypes'
import { StyledContainerColumn } from '../../../../styles/ContainerStyles'
import ConfirmationDialog from '../../../Dialog/ConfirmationDialog'
import FilterNotificationsPopup from '../../../FilterMenu/FilterNotificationsPopup'
import NotificationCard from '../NotificationCard'
import NotificationIcon from './NotificationIcon'
import ReturnButton from './ReturnButton'
import NotificationSidebarTitle from './NotificationSidebarTitle'
import SortAndFilterButtons from './SortAndFilterButtons'
import { formatStringDate } from '../../../../store/utils/dateUtils'

export type PushNotificationFilterType = {
  isRead: boolean[]
  reason: NotificationReason[]
  sortOrder: number
}

export const initialPushNotificationFilters: PushNotificationFilterType = {
  isRead: [],
  reason: [],
  sortOrder: 1,
}

const NotificationSidebar = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const currentUser = useSelector(getCurrentUser)
  const { displayNotification } = useNotification()

  const [open, setOpen] = useState<boolean>(false)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isSortAsc, setIsSortAsc] = useState<boolean>(false)
  const [isMarkAllConfirmationDialogOpen, setIsMarkAllConfirmationDialogOpen] = useState(false)
  const [isAcceptVacationConfirmationDialogOpen, setIsAcceptVacationConfirmationDialogOpen] = useState(false)
  const [isAcceptVacationInfoDialogOpen, setIsAcceptVacationInfoDialogOpen] = useState(false)
  const [notifications, setNotifications] = useState<PushNotification[]>([])
  const [acceptedVacation, setAcceptedVacation] = useState<Vacation | null>(null)
  const [acceptedNotificationId, setAcceptedNotificationId] = useState<string | null>(null)
  const [selectedFilters, setSelectedFilters] = useState<PushNotificationFilterType>(initialPushNotificationFilters)
  const [isLastPage, setIsLastPage] = useState(false)

  const clientRef = useRef<ReturnType<typeof createWsClient> | null>(null)
  const filtersRef = useRef<PushNotificationFilterType>(initialPushNotificationFilters)
  const sortAscRef = useRef<boolean>(false)
  const lastTokenRef = useRef<string | null | undefined>(undefined)
  const PAGE_SIZE = 10

  const baseQueryParams = useMemo<NotificationProps>(
    () => ({
      page: 0,
      size: PAGE_SIZE,
      'forwardedTo.id': currentUser?.id,
      dispatchStatus: NotificationDispatchStatus.DISPATCHED,
      type: NotificationType.PUSH,
      sortField: 'createdAt',
    }),
    [currentUser?.id]
  )

  const [requestParams, setRequestParams] = useState({
    ...baseQueryParams,
    isRead: selectedFilters.isRead?.length === 1 ? selectedFilters.isRead[0] : undefined,
    reason: selectedFilters.reason?.length > 0 ? selectedFilters.reason : undefined,
    sortOrder: selectedFilters.sortOrder,
  })

  const {
    data: pushNotificationsResponse,
    isSuccess,
    isLoading,
    isError,
  } = useGetPushNotificationsQuery(requestParams, { skip: !open })

  const { data: unseenNotificationsResponse } = useGetPushNotificationsQuery({
    ...baseQueryParams,
    size: 5,
    isRead: false,
  })

  const [checkVacationExists] = useLazyCheckVacationExistsQuery()

  useEffect(() => {
    if (pushNotificationsResponse && isSuccess) {
      setNotifications(prev =>
        requestParams.page === 0
          ? pushNotificationsResponse.content
          : [...prev, ...pushNotificationsResponse.content.filter(n => !prev.some(p => p.id === n.id))]
      )
      setIsLastPage(pushNotificationsResponse.content.length < PAGE_SIZE)
    }
  }, [pushNotificationsResponse, isSuccess, requestParams.page])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target.scrollHeight - target.scrollTop <= target.clientHeight && !isLoading && !isLastPage) {
      setRequestParams(prev => ({ ...prev, page: prev.page + 1 }))
    }
  }

  useEffect(() => {
    setRequestParams(prev => {
      return {
        ...prev,
        isRead: selectedFilters.isRead?.length === 1 ? selectedFilters.isRead[0] : undefined,
        reason: selectedFilters.reason?.length > 0 ? selectedFilters.reason : undefined,
        sortOrder: selectedFilters.sortOrder,
        page: 0,
      }
    })
  }, [selectedFilters])

  useEffect(() => {
    filtersRef.current = selectedFilters
  }, [selectedFilters])

  useEffect(() => {
    sortAscRef.current = isSortAsc
  }, [isSortAsc])

  useEffect(() => {
    if (!currentUser?.id) return
    let intervalId: ReturnType<typeof setInterval> | null = null

    const createAndActivateClient = async () => {
      if (clientRef.current) {
        try {
          await clientRef.current.deactivate()
        } catch (e) {}
        clientRef.current = null
      }

      if (UserService.isTokenExpired()) {
        try {
          await UserService.updateToken(() => {})
        } catch (e) {
          return
        }
      }

      const token = UserService.getToken()
      lastTokenRef.current = token

      let origin
      try {
        origin = new URL(environment.baseUrl).origin
      } catch {
        origin = environment.baseUrl.replace(/\/+$/, '')
      }

      const wsUrl = `${origin}/ws`

      const client = createWsClient({
        wsUrl,
        token,
        onConnect: () => {
          try {
            client.publish({ destination: '/app/register', body: JSON.stringify(currentUser.id) })
          } catch (exception) {}

          try {
            client.subscribe(`/notification/user/${currentUser.id}`, message => {
              try {
                const notification: PushNotification = JSON.parse(message.body)
                const filterType = filtersRef.current
                const asc = sortAscRef.current
                if (filterType.isRead?.length === 1 && notification.isRead !== filterType.isRead[0]) return
                if (filterType.reason?.length && !filterType.reason.includes(notification.reason)) return
                if (
                  notification.dispatchStatus &&
                  notification.dispatchStatus !== NotificationDispatchStatus.DISPATCHED
                )
                  return

                dispatch(pushNotificationSlice.util.invalidateTags(['PushNotification']))

                setNotifications(prev => {
                  if (prev.some(p => p.id === notification.id)) return prev
                  const newNotifications = asc ? [...prev, notification] : [notification, ...prev]

                  return newNotifications
                })
              } catch (exception) {}
            })
          } catch (exception) {}
        },
        onWebSocketClose: () => {},
      })

      client.activate()
      clientRef.current = client
    }

    createAndActivateClient()

    intervalId = setInterval(async () => {
      try {
        const currentToken = UserService.getToken()

        if (!clientRef.current) {
          await createAndActivateClient()
          return
        }

        if (currentToken && currentToken !== lastTokenRef.current) {
          await createAndActivateClient()
          return
        }

        if (UserService.isTokenExpired()) {
          try {
            await UserService.updateToken(() => {})
            await createAndActivateClient()
          } catch (e) {}
        }
      } catch (e) {}
    }, 30 * 1000)

    return () => {
      if (intervalId) clearInterval(intervalId)
      if (clientRef.current) {
        clientRef.current.deactivate().then(_ => {})
        clientRef.current = null
      }
    }
  }, [currentUser?.id, dispatch])

  const handleToggleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setIsFilterMenuOpen(prev => !prev)
  }

  const toggleSortButton = () => {
    const newSortOrder = isSortAsc ? 1 : 0

    setIsSortAsc(prev => !prev)
    setSelectedFilters(prev => ({
      ...prev,
      sortOrder: newSortOrder,
    }))
  }

  const toggleMarkAllButton = () => {
    setIsMarkAllConfirmationDialogOpen(true)
  }

  const toggleAcceptVacationButton = async (vacation: Vacation, notificationId: string) => {
    if (!vacation) return

    const result = await checkVacationExists(vacation.id.toString()).unwrap()

    setAcceptedNotificationId(notificationId)
    if (result) {
      setIsAcceptVacationConfirmationDialogOpen(true)
    } else {
      setIsAcceptVacationInfoDialogOpen(true)
    }
  }

  const [markAllRead, { isLoading: isMarkAllLoading }] = useMarkAllPushNotificationsReadMutation()
  const [markRead] = useMarkPushNotificationReadMutation()

  const handleConfirmMarkAllSeen = async () => {
    setIsMarkAllConfirmationDialogOpen(false)
    try {
      await markAllRead().unwrap()
    } catch (error) {
      displayNotification({
        open: false,
        type: 'error',
        message: t('components.notificationSidebar.markAllSeenErrorMessage'),
        timeout: 5000,
      })
    }
  }

  const [acknowledgeVacation] = useAcknowledgeVacationMutation()

  const handleConfirmAcceptVacation = useCallback(async () => {
    setIsAcceptVacationConfirmationDialogOpen(false)
    try {
      if (acceptedVacation) {
        await acknowledgeVacation(acceptedVacation.id.toString()).unwrap()
        if (acceptedNotificationId) {
          await markRead(acceptedNotificationId).unwrap()
        }
        displayNotification({
          open: false,
          type: 'success',
          message: t('components.notificationSidebar.acceptVacationSuccessMessage'),
          timeout: 5000,
        })
      }
    } catch (exception) {
      displayNotification({
        open: false,
        type: 'error',
        message: t('components.notificationSidebar.acceptVacationErrorMessage'),
        timeout: 5000,
      })
    }
    setAcceptedVacation(null)
    setAcceptedNotificationId(null)
  }, [acceptedVacation, acceptedNotificationId, acknowledgeVacation, markRead, displayNotification])

  const handleConfirmInfoVacation = () => {
    setIsAcceptVacationInfoDialogOpen(false)
  }

  const renderAcceptVacationContent = () => (
    <>
      {formatStringDate(acceptedVacation?.startDate)} - {formatStringDate(acceptedVacation?.endDate)}
      <br /> <br />
      {t('components.notificationSidebar.dialog.markAsAcknowledgedQuestion')}
      <br />
      {acceptedVacation?.description}
    </>
  )

  const handleDrawerClose = () => {
    setOpen(false)
    setSelectedFilters(prev => ({
      ...prev,
      isRead: [],
      reason: [],
    }))
  }

  return (
    <>
      <NotificationIcon
        setOpen={() => setOpen(true)}
        unseenNotificationsCount={unseenNotificationsResponse?.totalElements ?? 0}
      />

      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { bgcolor: 'background.main', overflowX: 'hidden' }, elevation: 0 }}
      >
        <Stack px={isDownSm ? 2.5 : 5.5} py={6} width={isDownSm ? '350px' : '570px'} height={'100%'}>
          <ReturnButton onClick={() => setOpen(false)} />
          <Stack sx={{ pt: 2, height: '100%' }}>
            <NotificationSidebarTitle isDownSm={isDownSm} />

            {isError ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 2,
                }}
              >
                <Typography color="error">{t('components.notificationSidebar.errorLoadingData')}</Typography>
                <Button>{t('components.notificationSidebar.retry')}</Button>
              </Box>
            ) : isLoading ? (
              <Box flex={1} p={3}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              </Box>
            ) : (
              <>
                <LoadingButton
                  onClick={toggleMarkAllButton}
                  disabled={isMarkAllLoading || unseenNotificationsResponse?.totalElements === 0}
                  size="small"
                  color="primary"
                  variant="contained"
                  sx={{ width: 'fit-content', alignSelf: 'end', mt: 2.5 }}
                >
                  <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
                  {t('components.notificationSidebar.markAllSeen')}
                </LoadingButton>

                <SortAndFilterButtons
                  isDownSm={isDownSm}
                  isSortAsc={isSortAsc}
                  isFilterMenuOpen={isFilterMenuOpen}
                  toggleSortButton={toggleSortButton}
                  handleToggleFilter={handleToggleFilter}
                  selectedFilters={selectedFilters}
                />

                <FilterNotificationsPopup
                  isOpen={isFilterMenuOpen}
                  setIsOpen={setIsFilterMenuOpen}
                  anchorEl={anchorEl}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />

                {notifications.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6">{t('components.notificationSidebar.noData')}</Typography>
                  </Box>
                ) : (
                  <Box
                    onScroll={handleScroll}
                    sx={{
                      flex: 1,
                      scrollbarGutter: 'stable',
                      mt: 2.5,
                      gap: 2,
                      overflow: 'auto',
                    }}
                  >
                    <StyledContainerColumn sx={{ gap: 2, height: 'auto', overflowY: 'auto', p: 0.5 }}>
                      {notifications.map(notification => (
                        <React.Fragment key={notification.id}>
                          <NotificationCard
                            notification={notification}
                            toggleAcceptVacationButton={toggleAcceptVacationButton}
                            setAcceptedVacation={setAcceptedVacation}
                          />
                        </React.Fragment>
                      ))}
                    </StyledContainerColumn>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Stack>
        <ConfirmationDialog
          open={isMarkAllConfirmationDialogOpen}
          setOpen={setIsMarkAllConfirmationDialogOpen}
          handleConfirm={handleConfirmMarkAllSeen}
          title={t('components.notificationSidebar.dialog.markAlltConfirmTitle')}
          content={t('components.notificationSidebar.dialog.markAllConfirmContent')}
        />
        <ConfirmationDialog
          open={isAcceptVacationConfirmationDialogOpen}
          setOpen={setIsAcceptVacationConfirmationDialogOpen}
          handleConfirm={handleConfirmAcceptVacation}
          title={t('components.notificationSidebar.dialog.vacationDetailsTitle')}
          content={renderAcceptVacationContent()}
        />
        <ConfirmationDialog
          open={isAcceptVacationInfoDialogOpen}
          setOpen={setIsAcceptVacationInfoDialogOpen}
          handleConfirm={handleConfirmInfoVacation}
          title={t('components.notificationSidebar.dialog.acceptVacationInfoTitle')}
          content={t('components.notificationSidebar.dialog.acceptVacationInfoContent')}
        />
      </Drawer>
    </>
  )
}

export default NotificationSidebar
